import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Copy, Trash2, ExternalLink, Eye, CheckCircle2, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { ArtistPageShare, ArtistPageView, Talent, TalentPagePendingEdit } from "@shared/schema";

type ApprovalMode = "off" | "talent_to_admin" | "rep_to_talent";

interface ShareWithFlag extends Omit<ArtistPageShare, "passwordHash"> {
  hasPassword?: boolean;
}

interface SettingsPayload {
  pressKitApprovalMode: ApprovalMode;
  pendingCount: number;
}

function ShareForm({ talentId, onCreated }: { talentId: string; onCreated: () => void }) {
  const { toast } = useToast();
  const [recipientLabel, setRecipientLabel] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [password, setPassword] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [agencyLogoUrl, setAgencyLogoUrl] = useState("");
  const [agencyPrimaryColor, setAgencyPrimaryColor] = useState("");
  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (recipientLabel) body.recipientLabel = recipientLabel;
      if (recipientEmail) body.recipientEmail = recipientEmail;
      if (password) body.password = password;
      if (expiresAt) body.expiresAt = new Date(expiresAt).toISOString();
      if (agencyName) body.agencyName = agencyName;
      if (agencyLogoUrl) body.agencyLogoUrl = agencyLogoUrl;
      if (agencyPrimaryColor) body.agencyPrimaryColor = agencyPrimaryColor;
      const res = await apiRequest("POST", `/api/talent/${talentId}/shares`, body);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Share link created" });
      setRecipientLabel(""); setRecipientEmail(""); setPassword(""); setExpiresAt(""); setAgencyName(""); setAgencyLogoUrl(""); setAgencyPrimaryColor("");
      queryClient.invalidateQueries({ queryKey: [`/api/talent/${talentId}/shares`] });
      onCreated();
    },
    onError: (e: Error) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">New share link</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Recipient label</Label><Input value={recipientLabel} onChange={(e) => setRecipientLabel(e.target.value)} placeholder="Acme Talent Agency" data-testid="input-share-label" /></div>
        <div><Label>Recipient email (optional)</Label><Input value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} type="email" data-testid="input-share-email" /></div>
        <div><Label>Password (optional)</Label><Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" data-testid="input-share-password" /></div>
        <div><Label>Expires (optional)</Label><Input value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} type="datetime-local" data-testid="input-share-expires" /></div>
        <div><Label>Agency name override</Label><Input value={agencyName} onChange={(e) => setAgencyName(e.target.value)} data-testid="input-share-agency-name" /></div>
        <div><Label>Agency logo URL</Label><Input value={agencyLogoUrl} onChange={(e) => setAgencyLogoUrl(e.target.value)} placeholder="https://…" data-testid="input-share-agency-logo" /></div>
        <div><Label>Agency primary color</Label><Input value={agencyPrimaryColor} onChange={(e) => setAgencyPrimaryColor(e.target.value)} placeholder="#1d4ed8" data-testid="input-share-agency-color" /></div>
        <div className="md:col-span-2 flex justify-end">
          <Button onClick={() => create.mutate()} disabled={create.isPending} data-testid="button-create-share">
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create share link"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SharesTable({ talentId }: { talentId: string }) {
  const { toast } = useToast();
  const { data = [], isLoading } = useQuery<ShareWithFlag[]>({ queryKey: [`/api/talent/${talentId}/shares`] });
  const { data: views = [] } = useQuery<ArtistPageView[]>({ queryKey: [`/api/talent/${talentId}/views`] });
  // Roll up per-share view counts + last-viewed timestamp once so the table
  // doesn't fan out a request per row.
  const viewStats = new Map<string, { count: number; last: Date }>();
  for (const v of views) {
    if (!v.shareId) continue;
    const cur = viewStats.get(v.shareId);
    const ts = new Date(v.viewedAt);
    if (!cur) viewStats.set(v.shareId, { count: 1, last: ts });
    else { cur.count += 1; if (ts > cur.last) cur.last = ts; }
  }
  const revoke = useMutation({
    mutationFn: async (shareId: string) => apiRequest("DELETE", `/api/talent/${talentId}/shares/${shareId}`),
    onSuccess: () => {
      toast({ title: "Revoked" });
      queryClient.invalidateQueries({ queryKey: [`/api/talent/${talentId}/shares`] });
    },
  });
  const copyUrl = (token: string) => {
    const url = `${window.location.origin}/artist/share/${token}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Link copied" });
  };
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (data.length === 0) return <div className="text-sm text-muted-foreground">No share links yet.</div>;
  return (
    <div className="space-y-2">
      {data.map((s) => {
        const stat = viewStats.get(s.id);
        return (
        <div key={s.id} className="flex items-center justify-between border rounded p-3" data-testid={`row-share-${s.id}`}>
          <div className="space-y-1">
            <div className="font-medium">{s.recipientLabel || s.recipientEmail || "Untitled share"}</div>
            <div className="text-xs text-muted-foreground flex gap-2 flex-wrap">
              {s.hasPassword && <Badge variant="outline">password</Badge>}
              {s.expiresAt && <span>expires {new Date(s.expiresAt).toLocaleString()}</span>}
              {s.revokedAt && <Badge variant="destructive">revoked</Badge>}
              {s.agencyName && <span>agency: {s.agencyName}</span>}
              <span data-testid={`share-views-${s.id}`}>
                {stat ? `${stat.count} view${stat.count === 1 ? "" : "s"} · last ${stat.last.toLocaleDateString()}` : "no views yet"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => copyUrl(s.token)} data-testid={`button-copy-share-${s.id}`}><Copy className="h-3 w-3 mr-1" />Copy</Button>
            <a href={`/artist/share/${s.token}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid={`button-open-share-${s.id}`}><ExternalLink className="h-3 w-3 mr-1" />Open</Button>
            </a>
            {!s.revokedAt && (
              <Button variant="ghost" size="sm" onClick={() => revoke.mutate(s.id)} data-testid={`button-revoke-share-${s.id}`}>
                <Trash2 className="h-3 w-3 mr-1" />Revoke
              </Button>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}

function ViewsLog({ talentId }: { talentId: string }) {
  const { data = [], isLoading } = useQuery<ArtistPageView[]>({ queryKey: [`/api/talent/${talentId}/views`] });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (data.length === 0) return <div className="text-sm text-muted-foreground">No views recorded yet.</div>;
  return (
    <div className="space-y-1 text-sm">
      {data.map((v) => (
        <div key={v.id} className="flex items-center justify-between border-b py-1" data-testid={`row-view-${v.id}`}>
          <span>{new Date(v.viewedAt).toLocaleString()}</span>
          <span className="text-muted-foreground text-xs">{v.shareId ? "share" : "slug"} · {v.userAgent?.slice(0, 40) || "—"}</span>
        </div>
      ))}
    </div>
  );
}

function PendingEditsAdmin() {
  const { toast } = useToast();
  const { data = [], isLoading } = useQuery<TalentPagePendingEdit[]>({ queryKey: ["/api/admin/press-kit-pending-edits"] });
  const decide = useMutation({
    mutationFn: async (input: { editId: string; talentId: string; decision: "approved" | "rejected"; note?: string }) =>
      apiRequest("POST", `/api/talent/${input.talentId}/pending-edits/${input.editId}/decide`, { decision: input.decision, decisionNote: input.note }),
    onSuccess: () => {
      toast({ title: "Decision recorded" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/press-kit-pending-edits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/press-kit-settings"] });
    },
  });
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
  if (data.length === 0) return <div className="text-sm text-muted-foreground">No pending edits.</div>;
  return (
    <div className="space-y-3">
      {data.map((e) => (
        <Card key={e.id} data-testid={`pending-edit-${e.id}`}>
          <CardContent className="pt-4 space-y-2">
            <div className="text-xs text-muted-foreground">
              {new Date(e.createdAt).toLocaleString()} · {e.requestedByRole}
            </div>
            <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-48">{JSON.stringify(e.payload, null, 2)}</pre>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => decide.mutate({ editId: e.id, talentId: e.talentId, decision: "rejected" })} data-testid={`button-reject-${e.id}`}>
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button size="sm" onClick={() => decide.mutate({ editId: e.id, talentId: e.talentId, decision: "approved" })} data-testid={`button-approve-${e.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SettingsPanel() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<SettingsPayload>({ queryKey: ["/api/admin/press-kit-settings"] });
  // Initialise + reset from server value so a Save without touching the
  // dropdown writes back the *current* mode, never the placeholder default.
  const [mode, setMode] = useState<ApprovalMode | null>(null);
  useEffect(() => {
    if (data && mode === null) setMode(data.pressKitApprovalMode);
  }, [data, mode]);
  const save = useMutation({
    mutationFn: async () => apiRequest("PUT", "/api/admin/press-kit-settings", { pressKitApprovalMode: mode }),
    onSuccess: () => {
      toast({ title: "Saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/press-kit-settings"] });
    },
  });
  if (isLoading || !data || mode === null) return <Loader2 className="h-4 w-4 animate-spin" />;
  const current = data.pressKitApprovalMode;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Approval workflow</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm text-muted-foreground">
          Current: <Badge variant="outline">{current}</Badge> · {data.pendingCount} pending
        </div>
        <div className="flex items-center gap-2">
          <Select value={mode} onValueChange={(v) => setMode(v as ApprovalMode)}>
            <SelectTrigger className="w-64" data-testid="select-approval-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off — anyone with permission edits directly</SelectItem>
              <SelectItem value="talent_to_admin">Talent edits queue for admin approval</SelectItem>
              <SelectItem value="rep_to_talent">Rep edits queue for talent approval</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-approval">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPressKitPage() {
  const { data: talents = [] } = useQuery<Talent[]>({ queryKey: ["/api/talent"] });
  const [selected, setSelected] = useState<string>("");
  const active = selected || talents[0]?.id || "";
  return (
    <div className="container max-w-5xl py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold" data-testid="text-press-kit-title">Press Kits</h1>
        <p className="text-sm text-muted-foreground">Manage public press-kit pages, share links, and the approval workflow.</p>
      </div>

      <SettingsPanel />

      <Card>
        <CardHeader><CardTitle className="text-base">Pending edits queue</CardTitle></CardHeader>
        <CardContent><PendingEditsAdmin /></CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Per-talent management</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Select value={active} onValueChange={setSelected}>
            <SelectTrigger className="w-72" data-testid="select-talent"><SelectValue placeholder="Select talent…" /></SelectTrigger>
            <SelectContent>
              {talents.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name} {t.pressKitPublished ? "" : "(unpublished)"}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {active && (
            <Tabs defaultValue="shares">
              <TabsList>
                <TabsTrigger value="shares" data-testid="tab-shares">Share links</TabsTrigger>
                <TabsTrigger value="views" data-testid="tab-views"><Eye className="h-4 w-4 mr-1" /> Views</TabsTrigger>
              </TabsList>
              <TabsContent value="shares" className="space-y-4 pt-4">
                <ShareForm talentId={active} onCreated={() => undefined} />
                <SharesTable talentId={active} />
              </TabsContent>
              <TabsContent value="views" className="pt-4">
                <ViewsLog talentId={active} />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
